import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Checkmark,
  Exit,
  Search,
  VisibilityOff,
  VisibilityOn,
} from '@bloomberg/media-ui-icons';

import { HelperText } from '../HelperText';
import { useSlug } from '../hooks/useSlug';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import { InputLabel } from '../InputLabel';
import { GlobalProps, InputProps } from '../types';
import { classes, createId } from '../utils';

import styles from './styles.module.css';

export type InputFieldProps = GlobalProps & {
  success?: boolean;
  className?: string;
  disabled?: boolean;
  disableHelperText?: boolean;
  error?: boolean;
  helperText?: string;
  id?: string;
  inputProps?: InputProps<HTMLInputElement>;
  label?: string;
  hideLabel?: boolean;
  name?: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  size?: 'small' | 'default';
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'password'
    | 'search'
    | 'tel'
    | 'url'
    | 'date';
  defaultValue?: string;
  value?: string;
  onClear?: () => void;
  variant?: 'default' | 'grayfill';
  customRightIcon?: React.ReactNode;
};

export const InputField = forwardRef<HTMLDivElement, InputFieldProps>(
  function InputFieldComponent(props, ref) {
    const {
      success = false,
      disabled,
      disableHelperText = false,
      id: userProvidedId,
      error = false,
      helperText,
      hideLabel = false,
      inputProps = {},
      label = '',
      name: userProvidedName,
      onChange,
      placeholder = '',
      required,
      size = 'default',
      type,
      defaultValue,
      value,
      unstyled,
      onClear,
      variant = 'default',
      customRightIcon,
    } = props;

    const labelText = required ? `${label}*` : label;
    const stableId = useRef(createId());
    const name = userProvidedName || 'text-input';
    const [slug] = useSlug(name);

    // inputProps defined values take precedence
    const requestedInputType = inputProps?.type || type;

    // create a unique id for inputs
    const id =
      userProvidedId ||
      inputProps?.id ||
      userProvidedName ||
      label ||
      stableId.current;

    // is the field currently active/focused
    const [active, setActive] = useState<boolean>(false);

    // should password be visible
    const [pwVisible, setPwVisible] = useState<boolean>(false);

    // does the field have input
    const [hasValue, setHasValue] = useState(
      Boolean(value?.length || defaultValue?.length || false)
    );

    useEffect(() => {
      setHasValue(Boolean(value?.length));
    }, [value]);

    const currentValue = {
      ...(value !== 'undefined' && {
        value,
      }),
      ...(defaultValue !== 'undefined' && {
        defaultValue,
      }),
    };

    // if cursor is dropped into field, a value exists, or a placeholder is provided
    // render the label in a shrink state
    const shrinkLabel =
      active ||
      hasValue ||
      Boolean(currentValue.value?.toString().length) ||
      Boolean(currentValue.defaultValue?.toString().length) ||
      Boolean(placeholder.length);

    const handleActiveChange = useCallback(
      (isActive: boolean) => {
        setActive(isActive);
      },
      [active]
    );

    const containerClasses = classes(
      [
        styles.inputContainer,
        styles[size],
        'css--input-base',
        styles[variant],
        type === 'search' && styles.searchBar,
      ],
      props
    );

    const labelClasses = classes(
      [
        'label',
        {
          active,
          [styles.error]: error,
          [styles.labelActive]: active,
        },
      ],
      {
        unstyled: props.unstyled,
        name: `${name}-label`,
      }
    );

    const inputClasses = classes(
      [
        // if custom styles are passed into input
        // use those before defaults
        styles.input,
        {
          [styles.error]: error,
          // input field marked active
          [styles.active]: active || Boolean(currentValue?.toString.length),
        },
        requestedInputType === 'password' ? styles.password : '',
      ],
      { unstyled: props.unstyled, name: `${name}-input` }
    );

    const iconClasses = classes([styles.icon], {
      unstyled: props.unstyled,
      name: `${name}-icon`,
    });

    // call upstream change handlers
    const handleChange = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.persist();

        setHasValue(ev.target.value.length > 0);

        if (inputProps.onChange) {
          inputProps.onChange(ev);
        } else if (onChange) {
          onChange(ev);
        }
      },
      [inputProps, onChange]
    );

    // call upstream focus handlers
    const handleFocus = useCallback(
      (ev: React.FocusEvent<HTMLInputElement>) => {
        ev.persist();
        if (inputProps.onFocus) {
          inputProps.onFocus(ev);
        }
      },
      [inputProps]
    );

    const finalPlaceholder = useMemo(() => {
      if (placeholder) {
        return placeholder;
      }

      return '';
    }, [placeholder, type]);

    const handleTogglePW = useCallback(() => {
      setPwVisible((v) => !v);
    }, [pwVisible]);

    // input type should be user defined EXCEPT
    // for when field is password and the user
    // opts in to "show password", in which case
    // we flip the input type to text
    const inputType =
      requestedInputType === 'password' && pwVisible
        ? 'text'
        : requestedInputType;

    return (
      <div className={containerClasses} ref={ref} data-component={slug}>
        <InputLabel
          className={classes(
            [
              styles.label,
              styles[size],
              {
                [styles.labelShrink]: shrinkLabel,
                [styles.hideLabel]: hideLabel,
                [styles.error]: error,
              },
            ],
            {
              unstyled: props.unstyled,
              name: `${name}-label-outer`,
            }
          )}
          spanClassName={labelClasses}
          label={labelText}
          unstyled={unstyled}
          size={size}
        >
          {type === 'search' && (
            <Search
              className={classes([
                styles.searchIcon,
                disabled && styles.disabled,
                styles[size],
              ])}
            />
          )}
          <Input
            inputProps={{
              ...inputProps,
              onFocus: handleFocus,
              type: inputType,
              'aria-required': required,
            }}
            {...currentValue}
            id={id}
            type={inputType}
            className={inputClasses}
            disabled={disabled}
            required={required}
            name={name}
            placeholder={finalPlaceholder}
            onActiveStatusChange={handleActiveChange}
            onChange={handleChange}
            unstyled={unstyled}
          />

          {!customRightIcon &&
            (type === 'password' || inputProps?.type === 'password') &&
            !success && (
              <span className={iconClasses}>
                <IconButton
                  type="button"
                  size="small"
                  onClick={handleTogglePW}
                  title={pwVisible ? 'Hide Password' : 'Show Password'}
                >
                  {pwVisible ? <VisibilityOn /> : <VisibilityOff />}
                </IconButton>
              </span>
            )}

          {!customRightIcon && type !== 'password' && onClear && hasValue && (
            <span className={iconClasses}>
              <IconButton
                type="button"
                size="small"
                onClick={onClear}
                title="Clear Input"
              >
                <Exit />
              </IconButton>
            </span>
          )}
          {customRightIcon && (
            <span className={iconClasses}>{customRightIcon}</span>
          )}

          {success && !error && (
            <span className={iconClasses}>
              <span className={styles.success}>
                <Checkmark />
              </span>
            </span>
          )}
        </InputLabel>
        {!disableHelperText && <HelperText error={error} text={helperText} />}
      </div>
    );
  }
);
